/**
 * Hooks para gestión de asistencia
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  meetingService,
  attendanceService,
  subscribeToMeetingsByClub,
  subscribeToMeetingsByClubAndPeriod,
  subscribeToMeetingsByType,
  subscribeToMeeting,
  subscribeToAttendanceByMeeting,
  subscribeToAttendanceByEvent,
  subscribeToAttendanceByPerson,
  subscribeToAttendanceByMember,
} from "@/lib/services/attendance";
import type {
  Meeting,
  Attendance,
  MeetingType,
  AttendanceStatus,
} from "@/types";
import { useAuthStore } from "@/stores/authStore";
import { Timestamp } from "firebase/firestore";
import { useRealtimeList, useRealtimeDoc } from "@/hooks/useRealtime";

// ==================== QUERY KEYS ====================

export const meetingKeys = {
  all: ["meetings"] as const,
  byClub: (clubId: string) => [...meetingKeys.all, "club", clubId] as const,
  byClubAndPeriod: (clubId: string, periodId: string) =>
    [...meetingKeys.all, "club", clubId, "period", periodId] as const,
  byType: (clubId: string, type: MeetingType) =>
    [...meetingKeys.all, "club", clubId, "type", type] as const,
  detail: (id: string) => [...meetingKeys.all, id] as const,
};

export const attendanceKeys = {
  all: ["attendance"] as const,
  byMeeting: (meetingId: string) =>
    [...attendanceKeys.all, "meeting", meetingId] as const,
  byEvent: (eventId: string) =>
    [...attendanceKeys.all, "event", eventId] as const,
  byPerson: (personId: string) =>
    [...attendanceKeys.all, "person", personId] as const,
  byMember: (memberId: string) =>
    [...attendanceKeys.all, "member", memberId] as const,
  stats: (meetingId: string) =>
    [...attendanceKeys.all, "stats", meetingId] as const,
  memberStats: (memberId: string) =>
    [...attendanceKeys.all, "memberStats", memberId] as const,
};

// ==================== MEETING HOOKS ====================

export function useMeetingsByClub(clubId: string | undefined) {
  return useRealtimeList<Meeting>(
    clubId ? (cb, onErr) => subscribeToMeetingsByClub(clubId, cb, onErr) : null,
    [clubId],
  );
}

export function useMeetingsByClubAndPeriod(
  clubId: string | undefined,
  periodId: string | undefined,
) {
  return useRealtimeList<Meeting>(
    clubId && periodId
      ? (cb, onErr) =>
          subscribeToMeetingsByClubAndPeriod(clubId, periodId, cb, onErr)
      : null,
    [clubId, periodId],
  );
}

export function useMeetingsByType(
  clubId: string | undefined,
  type: MeetingType | undefined,
) {
  return useRealtimeList<Meeting>(
    clubId && type
      ? (cb, onErr) => subscribeToMeetingsByType(clubId, type, cb, onErr)
      : null,
    [clubId, type],
  );
}

export function useMeeting(id: string | undefined) {
  return useRealtimeDoc<Meeting>(
    id ? (cb, onErr) => subscribeToMeeting(id, cb, onErr) : null,
    [id],
  );
}

export function useCreateMeeting() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: (
      data: Omit<
        Meeting,
        "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
      >,
    ) => meetingService.create(data, user?.uid || ""),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: meetingKeys.byClub(variables.clubId),
      });
      toast.success("Reunión creada correctamente");
    },
    onError: () => {
      toast.error("Error al crear la reunión");
    },
  });
}

export function useUpdateMeeting() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Meeting>;
      clubId: string;
    }) => meetingService.update(id, data, user?.uid),
    onSuccess: (_, { id, clubId }) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.byClub(clubId) });
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(id) });
      toast.success("Reunión actualizada");
    },
    onError: () => {
      toast.error("Error al actualizar la reunión");
    },
  });
}

export function useCloseMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      attendanceCount,
      totalMembers,
    }: {
      id: string;
      attendanceCount: number;
      totalMembers?: number;
      clubId: string;
    }) => meetingService.closeMeeting(id, attendanceCount, totalMembers),
    onSuccess: (_, { id, clubId }) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.byClub(clubId) });
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(id) });
      toast.success("Reunión cerrada");
    },
    onError: () => {
      toast.error("Error al cerrar la reunión");
    },
  });
}

export function useReopenMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; clubId: string }) =>
      meetingService.reopenMeeting(id),
    onSuccess: (_, { id, clubId }) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.byClub(clubId) });
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(id) });
      toast.success("Reunión reabierta");
    },
    onError: () => {
      toast.error("Error al reabrir la reunión");
    },
  });
}

export function useDeleteMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; clubId: string }) =>
      meetingService.delete(id),
    onSuccess: (_, { clubId }) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.byClub(clubId) });
      toast.success("Reunión eliminada");
    },
    onError: () => {
      toast.error("Error al eliminar la reunión");
    },
  });
}

// ==================== ATTENDANCE HOOKS ====================

export function useAttendanceByMeeting(meetingId: string | undefined) {
  return useRealtimeList<Attendance>(
    meetingId
      ? (cb, onErr) => subscribeToAttendanceByMeeting(meetingId, cb, onErr)
      : null,
    [meetingId],
  );
}

export function useAttendanceByEvent(eventId: string | undefined) {
  return useRealtimeList<Attendance>(
    eventId
      ? (cb, onErr) => subscribeToAttendanceByEvent(eventId, cb, onErr)
      : null,
    [eventId],
  );
}

export function useAttendanceByPerson(personId: string | undefined) {
  return useRealtimeList<Attendance>(
    personId
      ? (cb, onErr) => subscribeToAttendanceByPerson(personId, cb, onErr)
      : null,
    [personId],
  );
}

export function useAttendanceByMember(memberId: string | undefined) {
  return useRealtimeList<Attendance>(
    memberId
      ? (cb, onErr) => subscribeToAttendanceByMember(memberId, cb, onErr)
      : null,
    [memberId],
  );
}

export function useMeetingStats(meetingId: string | undefined) {
  return useQuery({
    queryKey: attendanceKeys.stats(meetingId!),
    queryFn: () => attendanceService.getMeetingStats(meetingId!),
    enabled: !!meetingId,
  });
}

export function useMemberAttendanceStats(
  memberId: string | undefined,
  startDate?: Timestamp,
  endDate?: Timestamp,
) {
  return useQuery({
    queryKey: attendanceKeys.memberStats(memberId!),
    queryFn: () =>
      attendanceService.getMemberStats(memberId!, startDate, endDate),
    enabled: !!memberId,
  });
}

export function useCreateAttendance() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: (
      data: Omit<
        Attendance,
        "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
      >,
    ) => attendanceService.create(data, user?.uid || ""),
    onSuccess: (_, variables) => {
      if (variables.meetingId) {
        queryClient.invalidateQueries({
          queryKey: attendanceKeys.byMeeting(variables.meetingId),
        });
        queryClient.invalidateQueries({
          queryKey: attendanceKeys.stats(variables.meetingId),
        });
      }
      if (variables.eventId) {
        queryClient.invalidateQueries({
          queryKey: attendanceKeys.byEvent(variables.eventId),
        });
      }
    },
    onError: () => {
      toast.error("Error al registrar asistencia");
    },
  });
}

export function useCreateBulkAttendance() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: ({
      meetingId,
      clubId,
      date,
      members,
      defaultStatus = "absent",
    }: {
      meetingId: string;
      clubId: string;
      date: Timestamp;
      members: Array<{
        memberId: string;
        personId: string;
        personName: string;
      }>;
      defaultStatus?: AttendanceStatus;
    }) =>
      attendanceService.createBulkForMeeting(
        meetingId,
        clubId,
        date,
        members,
        defaultStatus,
        user?.uid || "",
      ),
    onSuccess: (_, { meetingId }) => {
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.byMeeting(meetingId),
      });
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.stats(meetingId),
      });
      toast.success("Asistencia inicializada");
    },
    onError: () => {
      toast.error("Error al inicializar asistencia");
    },
  });
}

export function useUpdateAttendanceStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
      justification,
    }: {
      id: string;
      status: AttendanceStatus;
      justification?: string;
      meetingId: string;
    }) => attendanceService.updateStatus(id, status, justification),
    onSuccess: (_, { meetingId }) => {
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.byMeeting(meetingId),
      });
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.stats(meetingId),
      });
    },
    onError: () => {
      toast.error("Error al actualizar estado");
    },
  });
}

export function useUpdateBulkAttendance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      updates,
    }: {
      updates: Array<{
        id: string;
        status: AttendanceStatus;
        justification?: string;
      }>;
      meetingId: string;
    }) => attendanceService.updateBulk(updates),
    onSuccess: (_, { meetingId }) => {
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.byMeeting(meetingId),
      });
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.stats(meetingId),
      });
      toast.success("Asistencia guardada");
    },
    onError: () => {
      toast.error("Error al guardar asistencia");
    },
  });
}
